// Static UI chrome text only - navigation labels, generic status/error copy. The DECK/ROADMAP
// VOCABULARY (status names, team names, stat-card labels, filter labels) is never hardcoded here;
// it arrives on every payload (RoadmapView.ReferenceData, RoadmapView.Profile, etc.) and is
// rendered from props, per decomposition.md section 6.2.
export const strings = {
  appName: "Evergreen Portfolio",
  // Owner ruling 2026-10-03: shared copy for VocabularySelect, the closed-list control used by
  // all six closed fields (project Status/Team, milestone Completion/Team, note Type/Status).
  common: {
    selectNone: "— None —",
    notInList: (value: string) => `${value} (not in list)`,
    // Owner ruling 2026-10-03, step two: shared copy for SuggestionInput, the free-text combobox
    // used by the five OPEN fields (project Lead/Business owner, product Owner, the two go-live
    // dates). The toggle button's only purpose is "show me everything", so one fixed label serves
    // every field it appears on - each already carries its own visible <label> via FormField.
    suggestionsToggle: "Show suggestions",
    // D-303: shared by Projects and Data quality - a view preference (not a typed filter), so
    // "Clear filters" on either page leaves it alone.
    hideCompleted: "Hide completed",
    hideCompletedHint: "Hides projects whose status is Completed. Post-production projects stay visible.",
  },
  aria: {
    primaryNav: "Primary",
  },
  nav: {
    roadmap: "Roadmap",
    projects: "Projects",
    products: "Products",
    decks: "Decks",
    archive: "Archive",
    import: "Import",
    admin: "Admin",
    audit: "Audit",
    dataQuality: "Data quality",
  },
  auth: {
    signIn: "Sign in",
    signOut: "Sign out",
    signingIn: "Signing you in…",
  },
  errors: {
    accessDeniedTitle: "You don't have access to this page",
    accessDeniedBody: "Your role does not grant this operation.",
    notFoundTitle: "Page not found",
    genericTitle: "Something went wrong",
    genericBody: "An unexpected error occurred. Try reloading the page.",
    correlationPrefix: "Reference: ",
  },
  loading: "Loading…",
  roadmap: {
    loading: "Loading the roadmap…",
    errorTitle: "The roadmap could not be loaded",
    // D-207: on a freshly created deployment, GET /api/roadmap fails (500) until an admin saves a
    // field-map profile (PUT /api/config/field-maps) - the read path throws
    // FieldMapProfileNotFoundException before any RoadmapView is produced, so this is the only
    // observable shape a first-day deployment has. The API attaches no reason code to this
    // specific failure (decisions.md D-210 excludes it from the coded set on purpose), so this
    // copy is offered as the most likely cause on an unattributed server error, not a certainty.
    firstDayHint:
      "If this is a new deployment, an administrator must save a field-map profile (Admin > Field maps) before a roadmap can be served.",
    emptyBoard: "No projects are on the board for the current filters.",
    // D-255: owner-requested flag for a project detail card with zero milestones. Real data, not
    // an edge case - 45 of 73 live projects carry none (see ProjectDetailCard.tsx). Reused
    // verbatim as the card badge text, the board chip popover's empty-state line, and half of the
    // badge's accessible name (the flag glyph beside it is aria-hidden decoration only).
    noMilestones: "No milestones",
    // Owner-requested "Notes" section on the roadmap: end-user-readable change lines derived from
    // the audit trail (GET /api/roadmap/changes). The SENTENCES themselves are the server's - the
    // narrator on IAuditManager builds each one from structured change facts and this app never
    // composes one from parts - so nothing below is a template with a hole in it. These are the
    // frame around the list: its heading, its window label, its empty and error states, and the
    // control that shows the rest.
    recentChanges: {
      title: "Recent changes",
      windowLabel: "Changes in the last 30 days",
      empty: "Nothing has changed in the last 30 days.",
      // Deliberately not the whole-page error treatment: the card is context beside the board,
      // and a decoration that could black out a working roadmap would be a decoration with a veto.
      error: "Recent changes could not be loaded.",
      loading: "Loading recent changes…",
      truncated: "Only the most recent changes are shown.",
      showAll: "Show all",
      showFewer: "Show fewer",
      aria: {
        section: "Recent changes",
        list: "Recent change lines, most recent first",
      },
    },
    aria: {
      board: "Roadmap board",
      milestones: "Milestones",
      statusLegend: "Status legend",
      summary: "Roadmap summary",
    },
    // Visible titles for the two filter chip rows (owner feedback: the rows read as bare
    // rectangles with no explanation). Formerly aria-label-only entries under `aria` above;
    // now each is a visible label the group references via aria-labelledby (FilterBar.tsx),
    // so the accessible name and the visible name are the same string by construction. The
    // trailing colon matches the groupToggle label's "Group by:" format (owner-specified).
    filters: {
      showArea: "Show area:",
      filterStatus: "Filter status:",
    },
    // D-278: the LIVE roadmap's Team/Product swimlane-grouping toggle. Team is the server-derived
    // default (D-147/D-245's own columns/lanes); Product is a client-side regroup of the same
    // board (decisions.md D-266/D-271/D-277). Not shown on the frozen/archived view (D-003/D-266):
    // a historical snapshot carries no product data.
    groupToggle: {
      label: "Group by:",
      aria: "Group swimlanes by",
      team: "Team",
      product: "Product",
      loading: "Loading product grouping…",
      errorTitle: "The product grouping could not be loaded",
      unassignedLabel: "Unassigned",
    },
    // D-280: each detail card gains the project's lead, its resolved business owner (own value,
    // else its product's, else the noOwner dash) and the assigned product's name when one exists -
    // resolved client-side from the same projects+products join the Team/Product toggle already
    // reads (see hooks/useProjectProductJoin.ts), now fetched once for both modes.
    detailCard: {
      lead: "Lead",
      businessOwner: "Business owner",
      noOwner: "—",
      expand: (projectName: string) => `Expand ${projectName} details`,
    },
    // D-280: the read-only "larger view" a detail card's Expand button opens (a shared Modal
    // component, src/components/Modal.tsx). Reuses the card's own labels above for owners.
    detailModal: {
      close: "Close",
      openProject: "Open project",
      goLive: "Go-live",
      notes: "Notes",
      product: "Product",
      // D-285/D-286: the project's own open risk/blocker notes (RiskItem.isOpen), a genuine
      // addition here - D-280 mapped this modal's "Notes" heading to ProjectView.description
      // only, and a reader with an open blocker had no way to see it without leaving the roadmap.
      openRisks: "Open risks & blockers",
    },
    // D-284: a stat card's click opens the list of projects the server counted for it
    // (StatCardValue.memberProjectKeys, D-283). The accessible name names the count AND the
    // label together so a screen-reader user hears what the click is for before opening it -
    // a card with zero members is not a button at all (StatCard.tsx), so this label is never
    // produced for an empty card.
    statCardModal: {
      openAriaLabel: (count: number, label: string) => `Show the ${count} projects counted by ${label}`,
      close: "Close",
      // Defensive only - reachable if a caller ever renders this modal for a card whose
      // membership is empty; the interactive path (StatCard.tsx) never opens it for one.
      empty: "No projects are counted here.",
      // Honest fallback for a member key the roadmap's own project data does not carry (D-284):
      // never silently dropped from the count the card already showed.
      missingProjects: (count: number) =>
        `${count} ${count === 1 ? "project" : "projects"} not shown (not on this roadmap view).`,
    },
    // D-285/D-286: the sixth stat card ("Open risks & blockers") counts NOTES rather than
    // projects, so it opens a DIFFERENT modal than D-284's projects list - one row per resolved
    // note (StatCardValue.memberNoteKeys), not per project. Deliberately its own aria-label
    // phrase rather than reusing statCardModal.openAriaLabel: that phrase names
    // memberProjectKeys.length, which is NOT this card's printed value (D-285 - live data: 6
    // notes sit on 3 projects), so reusing it would announce the wrong number.
    notesCardModal: {
      openAriaLabel: (count: number) => `Show the ${count} open risks and blockers`,
      // Optional context line: the distinct projects those notes sit on
      // (StatCardValue.memberProjectKeys for this card's kind, D-285) - resolved, never re-counted.
      acrossProjects: (count: number) => `across ${count} ${count === 1 ? "project" : "projects"}`,
      // Honest fallback for a note key the roadmap's own project data does not carry, mirroring
      // D-284's missingProjects wording for the same reason: never silently dropped.
      missingNotes: (count: number) => `${count} ${count === 1 ? "note" : "notes"} not shown (not on this roadmap view).`,
    },
  },
  projects: {
    heading: "Projects",
    loading: "Loading projects…",
    errorTitle: "The project list could not be loaded",
    newProject: "New project",
    tableCaption: "Projects, sorted by the selected column",
    emptyState: "No projects match the current filters.",
    aria: {
      pagination: "Project list pages",
    },
    pagination: {
      itemsToShow: "Items to show",
      all: "All",
      previous: "Previous",
      next: "Next",
    },
    columns: {
      name: "Name",
      team: "Team",
      status: "Status",
      percentComplete: "% complete",
      updated: "Last updated",
    },
    filters: {
      name: "Name contains",
      team: "Team",
      status: "Status",
      multiSelectHint: "Hold Ctrl (or Cmd on Mac) to select more than one.",
      clear: "Clear filters",
    },
    detail: {
      loading: "Loading the project…",
      errorTitle: "The project could not be loaded",
      newHeading: "New project",
      sectionHeading: "Project details",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete project",
      saving: "Saving…",
      saved: "Project saved.",
      backToList: "Back to projects",
      fields: {
        name: "Project name",
        team: "Team",
        // Relabelled from "Owner" to "Project lead" (D-279/D-280): the STORED field and its
        // FieldDiff/banner mapping (Owner) are unchanged - this is a screen-only rename, kept here
        // as a comment rather than a second field, because operators reported the plain "Owner"
        // label ambiguous once a project also carries a distinct "Business owner".
        owner: "Project lead",
        // New in this batch (D-279/D-280): a project's own business owner, or null to inherit the
        // assigned product's - the inheritance is shown in the field's description slot, never
        // pre-filled into the input (ProjectForm.tsx keeps typed and inherited visibly distinct).
        businessOwner: "Business owner",
        description: "Description",
        status: "Status",
        percentComplete: "% complete",
        originalGoLive: "Original go-live estimate",
        currentGoLive: "Current go-live projection",
        businessProjectId: "Business Project ID",
        subgroup: "Subgroup",
        featureDetails: "Feature details",
        product: "Product",
      },
      // D-279's inheritance display: shown in the Business owner field's description slot when the
      // draft's own value is empty and a product is assigned - never pre-filled into the input,
      // so typed and inherited stay visibly distinct.
      businessOwnerInherited: (productName: string, ownerName: string) => `Inherited from ${productName}: ${ownerName}`,
      productNone: "None",
      // D-268: a project keeps a retired product until somebody CHANGES the assignment - the picker
      // says so rather than silently dropping the option, which would make the form lie about what
      // the project actually carries.
      productRetiredSuffix: " (retired)",
      duplicateBusinessId:
        "This project's business ID is shared by more than one project. Both are kept; consider correcting one of them.",
      ownerSuggestionUnit: "project",
      // Copy for the non-blocking save-result banner (WarningsBanner via lib/saveBanners.ts).
      // Each save-response code that can produce a banner line gets its own template here, kept
      // out of saveBanners.ts so the composition logic (grouping/dedup/field-naming) stays
      // separate from the words a user reads.
      saveWarnings: {
        fieldsNotWritten: (fieldLabels: readonly string[]) =>
          fieldLabels.length === 1
            ? `Your change to "${fieldLabels[0]}" wasn't saved: someone else updated this project at the same ` +
              "time, and their value was kept. The current value is shown now - edit it again if you still want " +
              "to change it."
            : `Your changes to ${fieldLabels.length} fields weren't saved: someone else updated this project at ` +
              `the same time, and their values were kept (${fieldLabels.join(", ")}). The current values are ` +
              "shown now - edit again if you still want those changes.",
        storeRefusedGeneric:
          "Your change wasn't saved, and the server didn't say why. Reload this project's data and try again.",
      },
    },
    milestones: {
      heading: "Milestones",
      add: "Add milestone",
      addAnother: "Save and add another",
      edit: "Edit",
      moveUp: "Move up",
      moveDown: "Move down",
      delete: "Delete milestone",
      empty: "No milestones yet.",
      fields: {
        description: "Description",
        date: "Date",
        completion: "Completion",
        isGoLive: "Go-live milestone",
        team: "Team",
      },
    },
    notes: {
      heading: "Notes",
      add: "Add note",
      edit: "Edit",
      delete: "Delete note",
      empty: "No notes yet.",
      fields: {
        type: "Type",
        text: "Note text",
        status: "Status",
      },
    },
    discardDialog: {
      title: "Discard unsaved changes?",
      body: "You have unsaved changes on this project. Leaving now will discard them.",
      confirm: "Discard changes",
      cancel: "Keep editing",
    },
  },
  products: {
    heading: "Products",
    loading: "Loading products…",
    errorTitle: "The product view could not be loaded",
    // D-266: v1 groups the Projects-list read path by product, client-side; this is the copy for
    // that grouped view, not for the admin catalogue (see admin.products below).
    technologyOwner: "Technology owner",
    businessOwner: "Business owner",
    noOwner: "—",
    projectCount: "projects",
    aria: {
      statusRollup: "Status rollup",
    },
    unassigned: {
      heading: "Unassigned",
      // The likely v1 starting state (D-266) - every project until an operator assigns one on the
      // project form - so this reads as a designed state, not an empty error.
      hint: "Projects are assigned to a product on the project form. Nothing here has been assigned to a product yet.",
    },
    projectColumns: {
      name: "Name",
      lead: "Lead",
      status: "Status",
      percentComplete: "% complete",
    },
    // D-280: a project row's own business owner shown only when it OVERRIDES the product's (i.e.
    // it is non-null and differs) - an inheriting or matching row shows nothing extra, since the
    // product's own owners line above the table already states the shared value.
    businessOwnerOverride: (name: string) => `Business owner: ${name}`,
    retiredBadge: "Retired",
  },
  dataQuality: {
    heading: "Data quality",
    loading: "Loading data-quality findings…",
    errorTitle: "The data-quality report could not be loaded",
    emptyState: "No data-quality findings at or above the selected severity.",
    minimumSeverity: "Minimum severity",
    projectLead: "Project lead",
    allLeads: "All leads",
    generatedAt: "Generated",
    severities: {
      Error: "Error",
      Warning: "Warning",
      Info: "Info",
    },
    // D-297: errorCount/warningCount/infoCount are whole-portfolio totals even while a lead
    // filter narrows the lists below - the "Portfolio totals" label says so plainly rather than
    // implying these three numbers describe only the filtered view.
    summary: {
      label: "Portfolio totals",
      errors: "errors",
      warnings: "warnings",
      info: "info",
    },
    columns: {
      type: "Type",
      finding: "Finding",
      project: "Project",
      lead: "Lead",
    },
  },
  decks: {
    heading: "Decks",
    tabs: {
      workflow: "Generate & publish",
      versions: "Versions",
    },
    generate: {
      heading: "Generate a preview",
      title: "Title",
      reportDate: "Report date",
      nextReportText: "Next report note",
      submit: "Generate preview",
      generating: "Generating preview…",
      errorTitle: "The preview could not be generated",
    },
    preview: {
      heading: "Preview",
      dataAsOf: "Data as of",
      slideCount: "Total slides",
      sections: "Slide sections",
      blockers: "Publish blockers",
      blockersHint: "These must be resolved before this preview can be published.",
      quality: "Data-quality findings at preview time",
      publish: "Publish this deck",
    },
    publish: {
      heading: "Publish",
      reportDate: "Report date (shown on the deck)",
      reportDateHint:
        "The report date is set for this meeting and is separate from the \"data as of\" stamp above, which reflects when the underlying data was captured.",
      markFinal: "Mark this version Final on publish",
      submit: "Publish",
      publishing: "Publishing…",
      confirmTitle: "Publish this deck?",
      confirmBody: "Publishing creates a new version visible to the whole portfolio audience.",
      confirmConfirm: "Publish",
      confirmCancel: "Cancel",
      errorTitle: "The deck could not be published",
    },
    versions: {
      heading: "Versions",
      loading: "Loading versions…",
      errorTitle: "The version list could not be loaded",
      empty: "No deck versions yet.",
      columns: {
        title: "Title",
        state: "Status",
        reportDate: "Report date",
        publishedBy: "Published by",
      },
      states: {
        Draft: "Draft",
        Published: "Published",
        Final: "Final",
        Archived: "Archived",
      },
      viewSnapshot: "View roadmap as of this version",
      viewDetail: "View",
    },
    detail: {
      heading: "Version detail",
      back: "Back to versions",
      loading: "Loading the version…",
      errorTitle: "The version could not be loaded",
      artifacts: "Artifacts",
      download: "Download",
      pdfPending:
        "The PDF has not been generated for this version yet. The PowerPoint (.pptx) is available now; a PDF may follow once the conversion queue processes it.",
      pdfGenerate: "Generate PDF",
      pdfGenerating: "Generating PDF…",
      pdfUnavailable: "The PDF converter is currently unavailable. Try again later, or use the PowerPoint file.",
      pdfError: "The PDF could not be generated.",
      finalize: "Finalize this version",
      finalizing: "Finalizing…",
      finalizeConfirmTitle: "Finalize this version?",
      finalizeConfirmBody:
        "Finalizing marks this version as the final record for its report date. This cannot be undone from here.",
      finalizeConfirmConfirm: "Finalize",
      finalizeConfirmCancel: "Cancel",
      compareHeading: "Compare with another version",
      compareLabel: "Comparand version key",
      compareSubmit: "Compare",
      compareErrorTitle: "The comparison could not be run",
    },
    snapshot: {
      bannerPrefix: "This is the roadmap as it stood on",
      bannerSuffix: "It will not reflect later changes.",
      back: "Back to version",
      loading: "Loading the archived roadmap…",
      errorTitle: "The archived roadmap could not be loaded",
    },
  },
  import: {
    heading: "Import",
    upload: {
      heading: "Upload a workbook",
      fileLabel: "Workbook file (.xlsx)",
      sourceProfile: "Source profile",
      submit: "Preview import",
      uploading: "Uploading and previewing…",
      tooLargeTitle: "That file is too large to upload",
      tooLargeBody: "Choose a smaller workbook, or split the data across more than one import.",
      errorTitle: "The workbook could not be previewed",
    },
    preview: {
      heading: "Preview",
      tabsFound: "Tabs found",
      rowCounts: "Rows by tab",
      unmapped: "Unmapped values",
      warnings: "Warnings",
      accept: "I have reviewed the warnings above",
      commit: "Commit import",
      committing: "Committing…",
      errorTitle: "The import could not be committed",
      commitDisabledHint: "Check the warnings box above to commit an import that produced warnings.",
    },
    result: {
      heading: "Import complete",
      projectsWritten: "Projects written",
      milestonesWritten: "Milestones written",
      notesWritten: "Notes written",
      requestsWritten: "Requests written",
      issues: "Issues",
      startAnother: "Start another import",
    },
    compare: {
      heading: "Compare two sources (dual-run)",
      baseline: "Baseline source profile",
      comparand: "Comparand source profile",
      submit: "Compare sources",
      comparing: "Comparing…",
      errorTitle: "The comparison could not be run",
      onlyInBaseline: "Only in baseline",
      onlyInComparand: "Only in comparand",
      differing: "Differing projects",
      identicalCount: "Identical projects",
    },
  },
  admin: {
    heading: "Admin",
    tabs: {
      referenceData: "Reference data",
      layout: "Layout",
      branding: "Branding",
      fieldMaps: "Field maps",
      users: "Users & roles",
      products: "Products",
    },
    aria: {
      tabsNav: "Admin sections",
    },
    referenceData: {
      heading: "Reference data",
      listPicker: "Reference list",
      loading: "Loading the list…",
      errorTitle: "The reference list could not be loaded",
      includeInactive: "Include retired values",
      addValue: "Add value",
      code: "Code",
      canonicalLabel: "Label",
      shortLabel: "Short label",
      colorHex: "Color",
      colorHexFormatHint: "Expected format: #RRGGBB (e.g. #1A2B3C). The server is the final authority on whether this is accepted.",
      displayOrder: "Display order",
      sortTier: "Sort tier",
      groupCode: "Group code",
      aliases: "Aliases (comma-separated)",
      active: "Active",
      edit: "Edit",
      retire: "Retire",
      reactivate: "Reactivate",
      save: "Save",
      cancel: "Cancel",
      saving: "Saving…",
      retireConfirmTitle: "Retire this value?",
      retireConfirmBody: "Retiring hides this value from new entries but keeps it on records that already use it.",
      retireConfirmConfirm: "Retire",
      retireConfirmCancel: "Cancel",
      retirementImpactHeading: "Retirement impact",
      retirementImpactLoading: "Checking usage…",
      retirementImpactRows: "referencing rows",
      activeValueCount: "active values",
      retiredValueCount: "retired",
      addTeamHint: "New teams require an explicit team-group assignment.",
      empty: "This list has no values yet.",
    },
    layout: {
      heading: "Layout settings",
      loading: "Loading layout settings…",
      errorTitle: "Layout settings could not be loaded",
      glanceSectionHeading: "Glance slides",
      glanceSectionDescription: "Row caps for the at-a-glance board slides. When a section exceeds its cap, the deck continues on another slide.",
      glanceRowCapWithStats: "Glance row cap (with stat cards)",
      glanceRowCapWithStatsHint: "Maximum project rows on a glance slide that also carries the stat cards.",
      glanceRowCapPlain: "Glance row cap (plain)",
      glanceRowCapPlainHint: "Maximum project rows on a glance slide without stat cards.",
      detailSectionHeading: "Detail slides",
      detailSectionDescription: "How many items each detail slide holds before the deck starts a new slide.",
      riskDecisionItemsPerSlide: "Risk/decision items per slide",
      riskDecisionItemsPerSlideHint: "Risk and decision items listed on one slide before the next slide begins.",
      requestsPerSlide: "Requests per slide",
      requestsPerSlideHint: "Requests listed on one slide before the next slide begins.",
      boardColumns: {
        heading: "Board columns",
        note: "Quarter-column policy is deployment configuration and is shown here for reference only; it is not editable on this screen.",
        forwardColumns: "Forward quarter columns",
        catchAll: "Catch-all column",
        catchAllOff: "Off",
        placeholders: "Placeholder columns",
        lifecycle: "Lifecycle columns",
        none: "None",
      },
      save: "Save layout settings",
      saving: "Saving…",
    },
    branding: {
      heading: "Branding",
      loading: "Loading branding…",
      errorTitle: "Branding could not be loaded",
      brandGroupHeading: "Brand palette",
      statusGroupHeading: "Status & stat-card tokens",
      tokensCaption: "Stored color tokens",
      addHeading: "Add a token",
      addColor: "Add color token",
      colorName: "Token name",
      colorValue: "Hex value",
      hexFormatHint: "Expected format: #RRGGBB (e.g. #1A2B3C). The server is the final authority on whether this is accepted.",
      empty: "No color tokens are stored yet.",
      save: "Save branding",
      saving: "Saving…",
    },
    fieldMaps: {
      heading: "Field-map profiles",
      loading: "Loading field-map profile…",
      errorTitle: "The field-map profile could not be loaded",
      noActiveProfileTitle: "First-time setup: activate a field-map profile",
      noActiveProfile: "No active field-map profile is configured. The roadmap and reports cannot be served until one is set.",
      profilesHeading: "Stored profiles",
      // D-254 closed D-253's API gap: FieldMapProfileSummary carries isActive now, stamped by the
      // Manager from the `source.activeProfile` setting every reader resolves its source context
      // from, so the active row is BADGED. The note is kept for the one case a badge cannot
      // express - the setting naming no stored profile, which is its unseeded default state.
      activeBadge: "Active",
      activeBadgeLabel: "This is the active source profile",
      noneActiveNote: "No profile is currently active: the deployment's active-source setting names none of the stored profiles.",
      // SAVING AND ACTIVATING ARE TWO ACTIONS NOW, AND THE COPY SAYS SO IN BOTH PLACES. D-254 had
      // to correct this form's wording because nothing in the save path wrote `source.activeProfile`
      // - a single-profile deployment worked through the readers' fallback, not through an
      // activation. With a real activation endpoint the honest split is a plain "save a profile"
      // form and a per-row "set active" action, so neither claims the other's effect.
      saveHeading: "Save a profile",
      saveDescription:
        "Saving stores a profile under its code. It does not decide which profile the deployment reads through - use “Set active” on a stored profile for that.",
      profileCode: "Profile code",
      sourceKind: "Source kind",
      bindings: "Entities / fields",
      updatedAt: "Last saved",
      view: "View",
      savedConfirmation: "Saved. The table above lists the stored profiles; activating one is a separate action.",
      save: "Save profile",
      saving: "Saving…",
      // The confirm exists because activation is not a screen-local change: it moves the setting
      // every roadmap read, import, export and publish resolves its source from, for everybody.
      setActive: "Set active",
      setActiveConfirmTitle: "Make this the active profile?",
      setActiveConfirmBody:
        "Activating changes which source every roadmap read, import and export is built from, for everyone. The change is recorded in the audit log.",
      setActiveConfirmConfirm: "Set active",
      setActiveConfirmCancel: "Cancel",
      activating: "Setting active…",
      activatedConfirmation: "Active profile updated. The badge above shows what this deployment now reads through.",
      activationErrorTitle: "The profile could not be activated",
    },
    users: {
      heading: "Users & roles",
      tableCaption: "Current portfolio users",
      search: "Search the directory",
      searchPlaceholder: "Search by name or email",
      searchHint: "Searches the organization directory; matching people who are not yet portfolio users appear below the current users.",
      loading: "Loading users…",
      errorTitle: "The user list could not be loaded",
      name: "Name",
      role: "Role",
      // Display labels for the PortfolioRoles API values. The VALUE sent to the API is always the
      // raw role string; this map only humanizes what a person reads.
      roleLabels: {
        Viewer: "Viewer",
        Contributor: "Contributor",
        ProjectManager: "Project Manager",
        Admin: "Admin",
      },
      assignRole: "Assign role",
      assigning: "Assigning…",
      confirmTitle: "Change this user's role?",
      confirmBody: "This changes what the user is authorized to do immediately.",
      confirmConfirm: "Assign role",
      confirmCancel: "Cancel",
      empty: "No users match the current search.",
      aria: {
        directoryResults: "Directory search results",
      },
    },
    products: {
      heading: "Products",
      loading: "Loading products…",
      errorTitle: "The product catalogue could not be loaded",
      includeRetired: "Include retired products",
      addProduct: "Add product",
      name: "Name",
      technologyOwner: "Technology owner",
      businessOwner: "Business owner",
      active: "Active",
      edit: "Edit",
      retire: "Retire",
      reactivate: "Reactivate",
      save: "Save",
      cancel: "Cancel",
      saving: "Saving…",
      empty: "No products have been created yet.",
      retireConfirmTitle: "Retire this product?",
      retireConfirmBody:
        "Retiring removes this product from every picker. Projects that already carry it keep it; nothing is deleted.",
      retireConfirmConfirm: "Retire",
      retireConfirmCancel: "Cancel",
      errorSaveTitle: "The product could not be saved",
    },
  },
  audit: {
    heading: "Audit",
    filters: {
      actor: "Actor",
      action: "Action",
      entityType: "Entity type",
      entityKey: "Entity key",
      from: "From",
      to: "To",
      apply: "Apply filters",
      clear: "Clear filters",
    },
    loading: "Loading audit entries…",
    errorTitle: "The audit log could not be loaded",
    empty: "No audit entries match the current filters.",
    columns: {
      atUtc: "When",
      actor: "Actor",
      action: "Action",
      entity: "Entity",
    },
    detail: {
      heading: "Entry detail",
      loading: "Loading the entry…",
      errorTitle: "The entry could not be loaded",
      notFoundHint: "This entry either does not exist or is outside your audit scope.",
      before: "Before",
      after: "After",
      context: "Context",
      close: "Close",
      view: "View detail",
    },
  },
} as const;
